import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Info,
  Loader2,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import {
  useOrgJoinStore,
  isCompanyEmail,
  getEmailDomain,
  maskEmail,
  type OrgJoinRequest,
} from '@/stores/org-join-store';

type Step = 'choose' | 'create' | 'join';

/**
 * Linear/Vercel Minimalist Organization Setup Chooser:
 * - Clean typography without cluttering section icons
 * - Refined duotone badge icons for choice cards
 * - Sleek action buttons without repetitive glyphs
 */
export function SharedAdminChooser({
  onClose,
  defaultStep,
  isSetup = true,
}: {
  onClose?: () => void;
  defaultStep?: Step;
  isSetup?: boolean;
}) {
  const invitedAdminList = useAuthStore((s) => s.invitedAdminList);
  const fetchInvitedAdminList = useAuthStore((s) => s.fetchInvitedAdminList);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const setSelectedAdmin = useAuthStore((s) => s.setSelectedAdmin);
  const user = useAuthStore((s) => s.user);
  const userEmail = user?.email;
  const extensionName = useAppStore((s) => s.extensionName);

  const createOrg = useOrgJoinStore((s) => s.createOrg);
  const domainAdmins = useOrgJoinStore((s) => s.domainAdmins);
  const domainAdminsLoading = useOrgJoinStore((s) => s.domainAdminsLoading);
  const domainAdminsLoaded = useOrgJoinStore((s) => s.domainAdminsLoaded);
  const domainAdminsReason = useOrgJoinStore((s) => s.domainAdminsReason);
  const loadDomainAdmins = useOrgJoinStore((s) => s.loadDomainAdmins);
  const sendJoinRequest = useOrgJoinStore((s) => s.sendJoinRequest);
  const cancelJoinRequest = useOrgJoinStore((s) => s.cancelJoinRequest);
  const sentRequests = useOrgJoinStore((s) => s.sentRequests);
  const subscribeSentRequests = useOrgJoinStore((s) => s.subscribeSentRequests);
  const loadMyChoice = useOrgJoinStore((s) => s.loadMyChoice);
  const choiceLoaded = useOrgJoinStore((s) => s.choiceLoaded);
  const existingOrgName = useOrgJoinStore((s) => s.orgName);
  const choiceDone = useOrgJoinStore((s) => s.choiceDone);

  const [step, setStep] = useState<Step>(defaultStep || 'choose');
  const [orgName, setOrgName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const autoJoinTried = useRef(false);
  const refetchedFor = useRef('');

  useEffect(() => {
    if (defaultStep) {
      setStep(defaultStep);
    }
  }, [defaultStep]);

  useEffect(() => {
    if (user?.email && extensionName && !choiceLoaded) {
      loadMyChoice();
    }
  }, [user?.email, extensionName, choiceLoaded, loadMyChoice]);

  useEffect(() => {
    if (invitedAdminList === null) fetchInvitedAdminList().catch(() => {});
  }, [invitedAdminList, fetchInvitedAdminList]);

  // Real-time subscription to sent join requests for instant auto-join
  useEffect(() => {
    if (!user?.email || !extensionName) return;
    const unsub = subscribeSentRequests();
    return () => unsub();
  }, [user?.email, extensionName, subscribeSentRequests]);

  const pendingInvites = useMemo(
    () => Object.entries(invitedAdminList || {}).filter(([, a]) => a.inviteStatus !== 'rejected'),
    [invitedAdminList],
  );

  // Admin accepted my request → their invite is in invitedAdminList → auto-switch
  useEffect(() => {
    if (autoJoinTried.current || selectedAdminUserId || choiceDone) return;
    const acceptedEmails = Object.values(sentRequests)
      .filter((r) => r.status === 'accepted')
      .map((r) => r.adminEmail.toLowerCase().trim());
    if (acceptedEmails.length === 0) return;
    const match = pendingInvites.find(([, a]) =>
      acceptedEmails.includes((a.invitedAdminUserEmail || '').toLowerCase().trim()),
    );
    if (!match) {
      const key = acceptedEmails.sort().join(',');
      if (refetchedFor.current !== key) {
        refetchedFor.current = key;
        fetchInvitedAdminList({ force: true }).catch(() => {});
      }
      return;
    }
    autoJoinTried.current = true;
    toast.success(
      `Your request was accepted — joining ${match[1].invitedAdminUserName || match[1].invitedAdminUserEmail}…`,
    );
    setSelectedAdmin(match[0], match[1].invitedAdminUserEmail);
  }, [sentRequests, pendingInvites, selectedAdminUserId, choiceDone, setSelectedAdmin, fetchInvitedAdminList]);

  useEffect(() => {
    if (step === 'join' && !domainAdminsLoaded && !domainAdminsLoading) {
      loadDomainAdmins();
    }
  }, [step, domainAdminsLoaded, domainAdminsLoading, loadDomainAdmins]);

  const handleSelectAdmin = useCallback(
    (adminUid: string, email?: string) => {
      const admin = invitedAdminList?.[adminUid];
      const targetEmail = email || admin?.invitedAdminUserEmail || null;
      toast.success(
        `Using ${admin?.invitedAdminUserName || targetEmail || 'admin'}'s account — switching…`,
      );
      setTimeout(async () => {
        await setSelectedAdmin(adminUid, targetEmail);
      }, 300);
    },
    [invitedAdminList, setSelectedAdmin],
  );

  const handleCreate = async () => {
    if (!orgName.trim()) return;
    setBusy('create');
    try {
      await createOrg(orgName);
      toast.success(`Organization "${orgName.trim()}" created successfully!`);
      if (selectedAdminUserId) await setSelectedAdmin(null, null);
      onClose?.();
    } catch {
      toast.error('Could not create organization. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const handleSend = async (email: string, name?: string | null) => {
    const target = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (target === userEmail?.toLowerCase()) {
      toast.error("You cannot send a request to yourself");
      return;
    }
    setBusy(target);
    try {
      await sendJoinRequest(target, name);
      toast.success('Join request sent to admin');
      if (target === adminEmail.trim().toLowerCase()) setAdminEmail('');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Could not send request. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async (email: string) => {
    setBusy(`cancel:${email}`);
    try {
      await cancelJoinRequest(email);
      toast.success('Join request cancelled');
    } catch {
      toast.error('Could not cancel request. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const statusBadge = (request?: OrgJoinRequest) => {
    if (!request) return null;
    const st = (request.status || 'pending').toLowerCase().trim();
    if (st === 'pending') {
      return (
        <span className="inline-flex items-center text-[11px] font-medium text-muted-foreground bg-muted rounded-md px-2 py-0.5">
          Pending
        </span>
      );
    }
    if (st === 'rejected') {
      return (
        <span className="inline-flex items-center text-[11px] font-medium text-muted-foreground bg-muted rounded-md px-2 py-0.5">
          Declined
        </span>
      );
    }
    return (
      <span className="inline-flex items-center text-[11px] font-medium text-muted-foreground bg-muted rounded-md px-2 py-0.5">
        Accepted
      </span>
    );
  };

  const requestList = (title: string, list: OrgJoinRequest[]) => (
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted-foreground">
        {title} ({list.length})
      </div>
      <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-muted/20">
        {list.map((r) => (
          <div key={r.adminEmail} className="flex items-center gap-3 px-3 py-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
              {(r.adminName || r.adminEmail).charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">{r.adminName || r.adminEmail}</div>
              {r.adminName && <div className="truncate text-xs text-muted-foreground">{r.adminEmail}</div>}
            </div>
            {statusBadge(r)}
            {r.status === 'pending' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCancel(r.adminEmail)}
                disabled={!!busy}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                {busy === `cancel:${r.adminEmail}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Cancel'}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const companyEmail = isCompanyEmail(userEmail);
  const domain = getEmailDomain(userEmail);

  const pendingSent = Object.values(sentRequests).filter((r) => r.status === 'pending');

  const otherSent = Object.values(sentRequests).filter(
    (r) => r.status !== 'accepted' && !domainAdmins.some((a) => a.email.toLowerCase().trim() === r.adminEmail.toLowerCase().trim()),
  );

  return (
    <div className="w-full">
      {/* ── STEP 1: CHOOSE (MINIMALIST CARDS) ── */}
      {step === 'choose' && (
        <div className="space-y-5 animate-fade-in">
          {/* Header */}
          <div className="text-center space-y-1">
            <h3 className="text-lg font-bold text-foreground">
              {isSetup ? 'Set up your organization' : 'Your organization'}
            </h3>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto">
              {isSetup
                ? "Create a new workspace or join your team's existing organization."
                : 'Continue with your current organization or join another one.'}
            </p>
          </div>

          {pendingSent.length > 0 && requestList('Pending requests', pendingSent)}

          {/* Cards */}
          <div className="grid gap-3 sm:grid-cols-2">
            {/* Card 1: Existing Org or Create Organization */}
            {existingOrgName || choiceDone || selectedAdminUserId ? (
              <button
                type="button"
                onClick={() => {
                  if (selectedAdminUserId) setSelectedAdmin(null, null);
                  onClose?.();
                }}
                className="group relative flex flex-col justify-between rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-foreground/30 hover:bg-muted/30 cursor-pointer focus:outline-none focus-visible:border-foreground/40"
              >
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted text-foreground">
                      <Building2 className="h-4.5 w-4.5" />
                    </div>
                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-md">
                      Active Admin
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {existingOrgName || 'Your organization'}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                      Your workspace is already set up as admin.
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between text-xs font-medium text-foreground">
                  <span>Continue as admin</span>
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                </div>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep('create')}
                className="group relative flex flex-col justify-between rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-foreground/30 hover:bg-muted/30 cursor-pointer focus:outline-none focus-visible:border-foreground/40"
              >
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted text-foreground">
                      <Building2 className="h-4.5 w-4.5" />
                    </div>
                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-md">
                      Admin
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      Create organization
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                      Set up a new workspace and invite your team.
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between text-xs font-medium text-foreground">
                  <span>Start as admin</span>
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                </div>
              </button>
            )}

            {/* Card 2: Join Organization */}
            <button
              type="button"
              onClick={() => setStep('join')}
              className="group relative flex flex-col justify-between rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-foreground/30 hover:bg-muted/30 cursor-pointer focus:outline-none focus-visible:border-foreground/40"
            >
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted text-foreground">
                    <Users className="h-4.5 w-4.5" />
                  </div>
                  {pendingInvites.length > 0 ? (
                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-md">
                      {pendingInvites.length} invite{pendingInvites.length > 1 ? 's' : ''}
                    </span>
                  ) : Object.keys(sentRequests).length > 0 ? (
                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-md">
                      Requested
                    </span>
                  ) : (
                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-md">
                      Member
                    </span>
                  )}
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Join organization
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    Ask your admin to add you or accept invites.
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between text-xs font-medium text-foreground">
                <span>Join team</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
              </div>
            </button>
          </div>

          <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <Info className="h-3 w-3 shrink-0" />
            You can be in only one organization at a time.
          </p>
        </div>
      )}

      {/* ── STEP 2: CREATE NEW ORGANIZATION ── */}
      {step === 'create' && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep('choose')}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          </div>

          <div className="space-y-1">
            <h3 className="text-base font-semibold text-foreground">Name your organization</h3>
            <p className="text-xs text-muted-foreground">
              Give your organization a name to get started as admin.
            </p>
          </div>

          <div className="space-y-1.5">
            <Input
              autoFocus
              placeholder="e.g. Acme Corp"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              maxLength={80}
              className="h-9.5 text-sm"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep('choose')}
              className="text-xs h-8"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={!orgName.trim() || busy === 'create'}
              className="text-xs h-8 px-3.5 font-medium"
            >
              {busy === 'create' ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Organization'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ── STEP 3: JOIN ORGANIZATION ── */}
      {step === 'join' && (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto scrollbar-none p-1.5 pb-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep('choose')}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          </div>

          <div className="space-y-1">
            <h3 className="text-base font-semibold text-foreground">Join someone's organization</h3>
            <p className="text-xs text-muted-foreground">
              Accept an invite or request access from your team admin.
            </p>
          </div>

          {/* Section 1: Pending Invites */}
          {pendingInvites.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground">
                Invites for you ({pendingInvites.length})
              </div>
              <div className="space-y-1.5">
                {pendingInvites.map(([uid, admin]) => {
                  const isSelected = uid === selectedAdminUserId;
                  const wasAccepted = sentRequests[(admin.invitedAdminUserEmail || "").toLowerCase().trim()]?.status === 'accepted';
                  return (
                    <div
                      key={uid}
                      className="flex items-center justify-between rounded-lg border border-border/50 bg-card p-2.5 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
                        {admin.invitedAdminUserPic && admin.invitedAdminUserPic !== 'null' ? (
                          <img
                            src={admin.invitedAdminUserPic}
                            alt=""
                            className="h-8 w-8 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-bold">
                            {(admin.invitedAdminUserName || admin.invitedAdminUserEmail || 'A')
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground truncate">
                            {admin.invitedAdminUserName || 'Admin'}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {admin.invitedAdminUserEmail}
                          </div>
                          {wasAccepted && (
                            <span className='mt-1 inline-flex items-center gap-1 rounded-md bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-950/30 dark:text-green-300'>
                              <CheckCircle2 className='h-3 w-3' /> Request accepted
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleSelectAdmin(uid, admin.invitedAdminUserEmail)}
                        disabled={busy === uid}
                        variant={isSelected ? 'outline' : 'default'}
                        className="font-medium text-xs h-7.5 px-3"
                      >
                        {busy === uid ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : isSelected ? (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Active
                          </>
                        ) : (
                          'Join'
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section 2: Sent Requests */}
          {otherSent.length > 0 && requestList('Your sent requests', otherSent)}

          {/* Section 3: Manual Admin Email */}
          <div className="space-y-2 pt-1">
            <div className="text-xs font-medium text-muted-foreground">
              Admin email
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="admin@company.com"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSend(adminEmail);
                }}
                className="h-8.5 text-xs"
              />
              <Button
                size="sm"
                onClick={() => handleSend(adminEmail)}
                disabled={!adminEmail.trim() || !!busy}
                className="h-8.5 px-3 text-xs font-medium"
              >
                {busy === adminEmail.trim().toLowerCase() ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  'Send request'
                )}
              </Button>
            </div>
          </div>

          {/* Section 4: Domain Admins */}
          {companyEmail && (
            <div className="space-y-2 pt-1">
              <div className="text-xs font-medium text-muted-foreground">
                Or pick a teammate at @{domain}
              </div>

              {domainAdminsLoading && (
                <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Looking for your team…</span>
                </div>
              )}

              {!domainAdminsLoading && domainAdminsReason === 'email_not_verified' && (
                <div className="rounded-lg bg-muted p-2.5 text-xs text-muted-foreground">
                  Verify your email to discover teammates from @{domain}, or enter your admin&apos;s email below.
                </div>
              )}

              {!domainAdminsLoading && domainAdminsReason === 'error' && (
                <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
                  <span>Couldn&apos;t load teammates.</span>
                  <button
                    type="button"
                    onClick={() => loadDomainAdmins()}
                    className="font-medium text-foreground underline-offset-2 hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!domainAdminsLoading && !domainAdminsReason && domainAdmins.length === 0 && (
                <div className="text-xs text-muted-foreground py-1">
                  No teammates from @{domain} registered as admin yet.
                </div>
              )}

              <div className="space-y-1.5">
                {domainAdmins.map((a) => {
                  const email = a.email.toLowerCase().trim();
                  const sent = sentRequests[email];
                  const isMember = pendingInvites.some(
                    ([, inv]) => (inv.invitedAdminUserEmail || '').toLowerCase().trim() === email,
                  );
                  const request = sent?.status === 'accepted' && !isMember ? undefined : sent;
                  return (
                    <div
                      key={a.uid}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border/50 bg-card p-2.5 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        {a.photoURL ? (
                          <img src={a.photoURL} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-bold">
                            {(a.name || a.email).charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {a.name || 'Admin'}{a.orgName ? ` · ${a.orgName}` : ''}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{maskEmail(a.email)}</div>
                        </div>
                      </div>

                      {request ? (
                        statusBadge(request)
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSend(a.email, a.name)}
                          disabled={!!busy}
                          className="text-xs h-7.5 px-3 font-medium"
                        >
                          {busy === a.email.toLowerCase() ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            'Request access'
                          )}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
